import { useMemo, useState, useCallback, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import _ from "lodash"

import { ImdfFeature } from "providers/venue/types"
import {
  DEFAULT_FILTER_VALUE,
  DINING_PAGE_CONFIG,
  DINING_TAXONOMY_TAG,
  GTM_VIEW_DINING_CATEGORY,
  Q_VENUE,
} from "constant"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue/useVenueController"
import { useGetOccupants } from "providers/venue/modules/data/occupants/useGetOccupants"
import { useTranslation } from "providers/i18n"

import { pushDataLayer } from "utils/googletagmanager"
import { useInfiniteGetOccupants } from "providers/venue/modules/data/occupants/useInfiniteGetOccupants"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import {
  createSortingConfigByOrdinalAndName,
  createSortingConfigFeatureByName,
} from "utils/feature"
import dayjs from "dayjs"
import { useRecommendedDirectory } from "hooks/sponsored-content/recommended"
import { useGetPageConfig } from "providers/venue/modules/data"

type FilterByVenueId = (venueId: string) => void
type FilterByCategoryId = (id: string) => void
type FilterByOrdinal = (ordinal: number) => void
type OrdinalFilterValue = number | "ALL"

const DINING_QUERY_KEY = ["dining"]

export const useDining = () => {
  const {
    i18n: { language: chosenLanguage },
  } = useTranslation()
  const {
    isMultiVenueProject,
    levels,
    venues,
    occupants,
    findFeatureById,
    dataLoaded,
    taxonomies = [],
  } = useVenue()
  const [category, setCategory] = useState(DEFAULT_FILTER_VALUE)
  const [ordinal, setOrdinal] =
    useState<OrdinalFilterValue>(DEFAULT_FILTER_VALUE)
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController()
  const [, setSearchParams] = useSearchParams()
  const { recommendedDirectories } = useRecommendedDirectory()

  const { layout = "list", metadata } = useGetPageConfig(DINING_PAGE_CONFIG)
  const { queryBy = "default" } = metadata || {}

  const sortingConfig = useMemo(() => {
    const baseSorting = [
      {
        field: "properties.is_featured",
        order: "desc",
      },
      {
        field: "properties.featured_image",
        order: "asc",
      },
      {
        field: "properties.logo",
        order: "asc",
      },
    ]

    const sortingStrategies = {
      group: () => [...createSortingConfigByOrdinalAndName(chosenLanguage)],
      default: () => [
        ...baseSorting,
        ...createSortingConfigFeatureByName(chosenLanguage),
      ],
    }

    return (sortingStrategies[layout] || sortingStrategies.default)()
  }, [layout, chosenLanguage])

  const filterByVenueId: FilterByVenueId = useCallback(
    (venueId) => {
      if (venueId) setSearchParams({ [Q_VENUE]: venueId }, { replace: true })
    },
    [setSearchParams]
  )

  const diningTaxonomyIds = useMemo(() => {
    return (
      taxonomies
        ?.filter((taxonomy) =>
          taxonomy.properties?.tags?.includes(DINING_TAXONOMY_TAG)
        )
        ?.map((taxonomy) => taxonomy.id) ?? []
    )
  }, [taxonomies])

  const categoryFilters = {
    taxonomy_tag: {
      local_category_ids: diningTaxonomyIds,
    },
    default: {
      category: "restaurant",
    },
  }

  /**
   * Result All Filtered Operating Restaurants on viewing venue
   */
  const { data: viewingVenueOperatingRestaurants } = useGetOccupants({
    queryKey: DINING_QUERY_KEY,
    pagination: {
      perPage: _.size(occupants),
    },
    sort: sortingConfig,
    filter: {
      properties: {
        anchor: {
          //@ts-ignore
          $exists: true,
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        ...categoryFilters[queryBy],
      },
    },
    enabled: dataLoaded,
  })

  /**
   * Result All Filtered Restaurants (without ordinal filter)
   * - Used to create an array of unique levels of restaurants that match the category
   * - Used as the source data for the useInfiniteGetOccupants hook
   */
  const { data: allFilteredRestaurantsSorted } = useGetOccupants({
    queryKey: DINING_QUERY_KEY,
    pagination: {
      perPage: _.size(viewingVenueOperatingRestaurants),
    },
    sort: sortingConfig,
    filter: {
      properties: {
        local_categories: {
          $elemMatch: {
            id: category,
          },
        },
        anchor: {
          //@ts-ignore
          $exists: true,
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        ...categoryFilters[queryBy],
      },
    },
    enabled: !!viewingVenueOperatingRestaurants,
  })

  /**
   * Result Infinite Filtered Restaurants
   * - Used to display data in an infinite scroll version
   */
  const {
    data: filteredRestaurantsSorted,
    hasNextPage,
    isFetched,
    fetchNextPage,
  } = useInfiniteGetOccupants({
    queryKey: DINING_QUERY_KEY,
    pagination: {
      perPage: 25,
    },
    sort: sortingConfig,
    filter: {
      properties: {
        local_categories: {
          $elemMatch: {
            id: category,
          },
        },
        anchor: {
          //@ts-ignore
          $exists: true,
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        //@ts-ignore
        ordinal,
        ...categoryFilters[queryBy],
      },
    },
    enabled: !!allFilteredRestaurantsSorted,
  })

  const filteredRestaurantAds = useMemo(() => {
    if (_.isEmpty(recommendedDirectories)) return []

    return recommendedDirectories.filter((ad) => {
      const adCategory = _.get(ad, "properties.category")
      return adCategory === "restaurant"
    })
  }, [recommendedDirectories])

  const restaurantSubCategories = useMemo(
    () =>
      _(viewingVenueOperatingRestaurants)
        //@ts-ignore
        .map((restaurant) => restaurant.properties.local_categories)
        .compact()
        .flatten()
        .uniqBy((category) => category.id)
        .filter((category) => category.properties.parent_id !== null)
        .sortBy("properties.order")
        .value(),
    [viewingVenueOperatingRestaurants]
  )

  const filteredLevels = useMemo(
    () =>
      _(allFilteredRestaurantsSorted)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find(
            (level: ImdfFeature) => level.properties.ordinal === ordinal
          )
        )
        .sortBy("properties.ordinal")
        .value(),
    [levels, allFilteredRestaurantsSorted]
  )

  const hasData = _.size(filteredRestaurantsSorted?.pages[0]?.data) > 0

  const filterByCategoryId: FilterByCategoryId = useCallback((id: string) => {
    if (id === null) return
    setCategory(id)
  }, [])

  const filterByOrdinal: FilterByOrdinal = useCallback(
    (ordinal: OrdinalFilterValue) => {
      if (ordinal === null) return
      setOrdinal(ordinal)
    },
    []
  )

  const onReachEndScroll = useCallback(
    (inView) => {
      if (inView && hasNextPage) fetchNextPage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasNextPage]
  )

  const { endScrollRef } = useInfiniteScroll({
    onReachEndScroll,
  })

  const processedRestaurantData = useMemo(() => {
    if (_.isEmpty(filteredRestaurantsSorted?.pages))
      return filteredRestaurantsSorted

    const currentDate = dayjs()
    const periodInDays = _.get(metadata, "newShop.periodInDays", 30)

    return {
      pages: filteredRestaurantsSorted.pages.map((page) => ({
        ...page,
        data: page.data.map((occupant) => {
          if (!occupant?.properties?.start_date) {
            return occupant
          }

          const startDate = dayjs(occupant.properties.start_date)
          const isNewShop =
            currentDate.isSameOrAfter(startDate) &&
            currentDate.diff(startDate, "day") <= periodInDays

          const isComingSoon = startDate.isAfter(currentDate)

          return {
            ...occupant,
            properties: {
              ...occupant.properties,
              isNewShop,
              isComingSoon,
            },
          }
        }),
      })),
      pageParams: filteredRestaurantsSorted.pageParams,
    }
  }, [metadata, filteredRestaurantsSorted])

  const attachedAdsResult = useMemo(() => {
    if (
      _.isEmpty(processedRestaurantData?.pages) ||
      _.isEmpty(filteredRestaurantAds)
    ) {
      return processedRestaurantData
    }

    const getItemId = (item) => _.get(item, "id")
    const getOrdinal = (item) => _.get(item, "properties.ordinal")

    const adsIds = new Set(filteredRestaurantAds.map(getItemId))

    // Filter out restaurants that have the same ID as advertisements
    const filterOutDuplicateAds = (items) => {
      return items.filter((item) => !adsIds.has(getItemId(item)))
    }

    // Group advertisements by floor (ordinal) สำหรับ group layout
    const adsByOrdinal = _.groupBy(filteredRestaurantAds, getOrdinal)
    const insertedOrdinals = new Set()

    const adsInsertionMethod = {
      /**
       * Group Layout Strategy
       * Inserts all advertisements at the beginning of each floor
       */
      group: (items) => {
        const uniqueItems = filterOutDuplicateAds(items)
        const itemsByOrdinal = _.groupBy(uniqueItems, getOrdinal)
        const ordinals = _.orderBy(
          Object.keys(itemsByOrdinal).map(Number),
          [],
          ["desc"]
        )

        return ordinals.flatMap((ordinal) => {
          const ads = adsByOrdinal[ordinal] || []
          const shouldInsert = ads.length > 0 && !insertedOrdinals.has(ordinal)

          if (shouldInsert) {
            insertedOrdinals.add(ordinal)
            return [...ads, ...itemsByOrdinal[ordinal]]
          }

          return itemsByOrdinal[ordinal]
        })
      },

      /**
       * List Layout Strategy
       * Inserts all advertisements at the very beginning (first page only)
       */
      list: (items, isFirstPage) => {
        const uniqueItems = filterOutDuplicateAds(items)

        if (isFirstPage) {
          return [...filteredRestaurantAds, ...uniqueItems]
        }

        return uniqueItems
      },
    }

    // Select insertion strategy based on layout type
    const insertAds = adsInsertionMethod[layout] || adsInsertionMethod.list

    // Apply ad insertion to all pages
    return {
      pages: processedRestaurantData.pages.map((page, pageIndex) => ({
        ...page,
        data:
          layout === "list"
            ? insertAds(page.data || [], pageIndex === 0)
            : insertAds(page.data || []),
      })),
      pageParams: processedRestaurantData.pageParams,
    }
  }, [processedRestaurantData, filteredRestaurantAds, layout])

  useEffect(() => {
    if (category && category !== DEFAULT_FILTER_VALUE) {
      pushDataLayer({
        event: GTM_VIEW_DINING_CATEGORY,
        dining_category_id: category,
        dining_category_name: _.get(
          findFeatureById(category),
          "properties.name.en"
        ),
      })
    }
  }, [findFeatureById, category])

  return {
    restaurants: attachedAdsResult,
    categories: restaurantSubCategories,
    currentCategory: category,
    levels: filteredLevels,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasNextPage,
    hasData,
    isFetched,
    endScrollRef,
    filterByCategoryId,
    filterByOrdinal,
    filterByVenueId,
    layout,
  }
}
